import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from '@sentry/react';

import { ThemeProvider } from '@/design-system/v2'
import { AuthProvider } from './contexts/AuthContext'
import App from './App.tsx'
import { ErrorFallback } from './ErrorFallback.tsx'
import { Toaster } from '@/components/ui/sonner'

import "./index.css"

// ============================================================================
// MONITORIZACIÓN: Sentry (RGPD-conforme — región EU, sin PII)
// Base legal: interés legítimo en detección de errores (sin datos personales)
// ============================================================================
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,                    // desactivado en desarrollo
  tracesSampleRate: 1.0,
  sendDefaultPii: false,                             // obligatorio RGPD
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Non-Error exception captured',
  ],
  beforeSend(event) {
    // Eliminar cualquier contexto extra y datos de usuario antes del envío
    delete event.extra;
    delete event.user;
    return event;
  },
});

// ============================================================================
// DEBUG: Funciones de diagnóstico expuestas en window (acceso desde consola)
// ============================================================================
import { runDiagnostic, quickTestDERA, quickGeocode } from './utils/geocodingDiagnostic'
import { 
  loadLocalData, 
  isDataLoaded, 
  getLoadStats, 
  searchLocal 
} from './lib/LocalDataService'
import { deconcatenateText } from './lib/textDeconcatenator'
import { GeocodingOrchestrator } from './services/geocoding/GeocodingOrchestrator'

// Exponer en window para diagnóstico desde consola
declare global {
  interface Window {
    ptelDiag: {
      runDiagnostic: typeof runDiagnostic;
      quickTestDERA: typeof quickTestDERA;
      quickGeocode: typeof quickGeocode;
      loadLocalData: typeof loadLocalData;
      isDataLoaded: typeof isDataLoaded;
      getLoadStats: typeof getLoadStats;
      searchLocal: typeof searchLocal;
      deconcatenateText: typeof deconcatenateText;
      GeocodingOrchestrator: typeof GeocodingOrchestrator;
    };
  }
}

window.ptelDiag = {
  runDiagnostic,
  quickTestDERA,
  quickGeocode,
  loadLocalData,
  isDataLoaded,
  getLoadStats,
  searchLocal,
  deconcatenateText,
  GeocodingOrchestrator,
};

console.log('🔧 [PTEL] Herramientas de diagnóstico disponibles en window.ptelDiag');
console.log('   Ejecuta: ptelDiag.runDiagnostic() para diagnóstico completo');

// ============================================================================
// E2E TESTING HELPERS (dev local + CI E2E con build de producción)
// Expone módulos en window.__PTEL_TEST__ para Playwright.
// ⚠️ VITE_E2E_HELPERS es opt-in. En builds de producción reales (pteltool.es)
//    debe estar ausente para que los helpers no se incluyan en el bundle.
// ============================================================================
if (import.meta.env.DEV || import.meta.env.VITE_E2E_HELPERS === 'true') {
  import('./lib/e2eTestHelpers').then(({ initE2ETestHelpers }) => {
    initE2ETestHelpers();
  }).catch(err => {
    console.warn('[E2E] Error cargando helpers de testing:', err);
  });
}
// ============================================================================

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ThemeProvider>
        <AuthProvider>
          <App />
          <Toaster />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>
)
